import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scale } from "lucide-react";
import { saveHorseWeight } from "@/components/DoseByWeight";
import { SaveHorseWeight } from "@/components/SaveHorseWeight";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const estimateWeight = (girth, height, young) => {
  const g = Number(girth), h = Number(height);
  if (!g || (!young && !h)) return null;
  const w = young ? 4.5 * g - 370 : 4.3 * g + 3 * h - 785;
  return w > 50 ? Math.round(w) : null;
};

export const MeasureHelp = () => (
  <div className="text-sm text-slate-600 space-y-2" data-testid="measure-help">
    <img src="/img/medir-caballo.jpg" alt="Cómo medir el perímetro torácico y la altura a la cruz" className="w-full rounded border border-slate-200 bg-white" />
    <p><b className="text-[#DC2626]">Perímetro torácico (rojo):</b> pasa la cinta alrededor del cuerpo justo detrás de la cruz y de los codos, ajustada pero sin apretar, al final de una espiración.</p>
    <p><b className="text-[#2563EB]">Altura a la cruz (azul):</b> con el caballo cuadrado en suelo llano, mide desde el suelo hasta el punto más alto de la cruz.</p>
  </div>
);

const AGES = [["adulto", "4 años o más"], ["joven", "Menos de 4 años"]];

export default function HorseWeight() {
  const [f, setF] = useState({ age: "adulto", girth: "", height: "" });
  const young = f.age === "joven";
  const w = estimateWeight(f.girth, f.height, young);
  useEffect(() => { saveHorseWeight(w); }, [w]);
  return (
    <div data-testid="weight-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#93C5FD] mb-3">Herramienta gratuita</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl" data-testid="weight-title">Calcula el peso de tu caballo</h1>
          <p className="text-blue-100 mt-4 max-w-2xl">Solo necesitas una cinta métrica. Conocer su peso es la base para ajustar la ración y las dosis de los complementos.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 py-12 grid lg:grid-cols-2 gap-10">
        <div className="space-y-5">
          <div>
            <Label>Edad del caballo</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {AGES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF({ ...f, age: v })} data-testid={`weight-age-${v}`}
                  className={`h-11 rounded-full border text-sm font-bold transition-colors ${f.age === v ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "border-slate-300 text-[#0B1D3A] hover:border-[#0B1D3A]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className={`grid gap-3 ${young ? "" : "grid-cols-2"}`}>
            <div><Label>Perímetro torácico (cm)</Label><Input type="number" min="60" max="260" placeholder="p. ej. 185" value={f.girth} onChange={(e) => setF({ ...f, girth: e.target.value })} data-testid="weight-girth-input" className="mt-1.5 h-11" /></div>
            {!young && <div><Label>Altura a la cruz (cm)</Label><Input type="number" min="70" max="210" placeholder="p. ej. 160" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} data-testid="weight-height-input" className="mt-1.5 h-11" /></div>}
          </div>
          <div className="rounded border-2 border-[#0B1D3A] p-6" data-testid="weight-result-box">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2"><Scale size={15} /> Peso estimado</p>
            <p className="font-display font-black text-5xl text-[#0B1D3A] mt-2" data-testid="weight-result">{w ? `${w} kg` : "—"}</p>
            <p className="text-xs text-slate-500 mt-3">Fórmula usada: {young ? "4,5 × perímetro torácico − 370" : "4,3 × perímetro torácico + 3 × altura a la cruz − 785"}. Es una estimación orientativa; la báscula es siempre lo más exacto.</p>
            {w && (
              <Link to={`/calculadora?peso=${w}`} data-testid="weight-to-diet" className="mt-5 inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold px-6 py-3 rounded-full transition-colors">
                Calcular su dieta con este peso <ArrowRight size={17} />
              </Link>
            )}
            {w && <SaveHorseWeight kg={w} source="medidas" id="weight-save" />}
          </div>
        </div>
        <MeasureHelp />
      </section>
    </div>
  );
}
